import "~/stylesheets/application.scss";
import "@vitejs/plugin-react/preamble";
import "~/api/client"; // Register axios interceptors for session expiration handling

import i18n from "~/locales/i18n";

import { Settings } from "luxon";
Settings.defaultLocale = i18n.language;

import { captureFirstTouchAttribution } from "~/utils/attribution";
captureFirstTouchAttribution();

// React
import React, { lazy, Suspense } from "react";
import { createRoot } from "react-dom/client";
import { ErrorBoundary } from "react-error-boundary";

import * as Sentry from "@sentry/react";
import "~/config/sentry";

import Loading from "~/components/shared/Loading";
import ErrorPage from "~/components/utils/ErrorPage";

// Each page is its own chunk, fetched only when its `data-component` is
// actually mounted, so e.g. Stripe (payments) or the flow canvas
// (scenarios/campaigns) never load on pages that don't need them.
const components = {
  NotFound: lazy(() => import("~/components/NotFound")),
  MaintenancePage: lazy(() => import("~/components/MaintenancePage")),
  LoginPage: lazy(() => import("~/components/login/LoginPage")),
  SignupPage: lazy(() => import("~/components/signup/SignupPage")),
  PublicAppointmentPage: lazy(
    () => import("~/components/appointments/PublicAppointmentPage"),
  ),
  PublicPaymentPage: lazy(
    () => import("~/components/payments/PublicPaymentPage"),
  ),
  RequestInvoicePage: lazy(
    () => import("~/components/payments/RequestInvoicePage"),
  ),
  PublicScenarioPage: lazy(
    () => import("~/components/automation/scenarios/PublicScenarioPage"),
  ),
  PublicSurveyMessagePage: lazy(
    () => import("~/components/surveys/PublicSurveyMessagePage"),
  ),
  PublicMediaUploadPage: lazy(
    () => import("~/components/content/uploads/PublicMediaUploadPage"),
  ),
  PublicMediaUploadExpiredPage: lazy(
    () => import("~/components/content/uploads/PublicMediaUploadExpiredPage"),
  ),
  PublicEmailPreferencesPage: lazy(
    () => import("~/components/emails/PublicEmailPreferencesPage"),
  ),
  PublicCampaignPage: lazy(
    () => import("~/components/campaigns/PublicCampaignPage"),
  ),
  PublicConnectInstagramPage: lazy(
    () =>
      import(
        "~/components/onboarding/steps/connect_instagram/PublicConnectInstagramPage"
      ),
  ),
  ShopifyLoginPage: lazy(() => import("~/components/shopify/ShopifyLoginPage")),
  ShopifyChooseOrganizationPage: lazy(
    () => import("~/components/shopify/ShopifyChooseOrganizationPage"),
  ),
  OauthLoginPage: lazy(() => import("~/components/oauth/OauthLoginPage")),
};

document.addEventListener("DOMContentLoaded", () => {
  const container = document.getElementById("app");
  if (!container) return;

  const name = container.getAttribute("data-component");
  if (!name) return;

  const Component = components[name];

  const props = JSON.parse(container.getAttribute("data-props"));

  if (Component) {
    const root = createRoot(container, {
      // Callback called when an error is thrown and not caught by an ErrorBoundary.
      onUncaughtError: Sentry.reactErrorHandler(console.error),
      // Callback called when React catches an error in an ErrorBoundary.
      onCaughtError: Sentry.reactErrorHandler(console.error),
      // Callback called when React automatically recovers from errors.
      onRecoverableError: Sentry.reactErrorHandler(console.error),
    });
    root.render(
      <ErrorBoundary FallbackComponent={ErrorPage}>
        <Suspense fallback={<Loading />}>
          <Component {...props} />
        </Suspense>
      </ErrorBoundary>,
    );
  }
});
